import {
  createContext,
  useContext,
  useMemo,
  type MouseEvent,
  type ReactNode
} from 'react';
import { Box } from '@material-ui/core';
import type { SpaceMember } from '@ably/spaces';
import { RowPresence } from 'pages/auto-rfq/pages/details/components/presence/row-presence';
import { useQuoteRowFocus } from './use-quote-row-focus';
import { useQuotePresenceStore } from './store';

interface QuoteRowPresenceContextValue {
  /** Other members keyed by the exact quote item / section id they're on. */
  rowFocus: Map<string, SpaceMember[]>;
  setHoveredItemId: (id: string | null) => void;
}

const QuoteRowPresenceContext = createContext<QuoteRowPresenceContextValue>({
  rowFocus: new Map(),
  setHoveredItemId: () => {}
});

/**
 * Holds the single Spaces locations subscription for the whole product table
 * and exposes the per-id focus map + hover setter to every row. Mounted inside
 * the quote's Space provider; each row reads ITS own id via `useQuoteRowPresence`
 * so options and section children show their own indicator, not the parent's.
 */
export const QuoteRowPresenceProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const rowFocus = useQuoteRowFocus();
  const setHoveredItemId = useQuotePresenceStore(s => s.setHoveredItemId);
  const value = useMemo(
    () => ({ rowFocus, setHoveredItemId }),
    [rowFocus, setHoveredItemId]
  );
  return (
    <QuoteRowPresenceContext.Provider value={value}>
      {children}
    </QuoteRowPresenceContext.Provider>
  );
};

/**
 * Per-row presence for one quote item / section. `members` are the OTHER
 * collaborators whose Spaces location points at THIS exact id. `rowProps` wires
 * hover so the row broadcasts its own id — `stopPropagation` lets a nested row
 * (option / section child) claim the hover instead of its parent, and the
 * guarded leave only clears when this row is still the hovered one.
 */
/**
 * Blue outline pulsed on a row right after a "follow" jump, matching the RFQ
 * card highlight. Apply with a `transition` so it fades in/out.
 */
export const HIGHLIGHT_BOX_SHADOW =
  '0 0 0 2px #1E96FC, 0 4px 16px rgba(30,150,252,0.35)';

export const useQuoteRowPresence = (itemId: string) => {
  const { rowFocus, setHoveredItemId } = useContext(QuoteRowPresenceContext);
  const members = rowFocus.get(itemId);
  // Subscribe to the boolean (not the id) so only the matching row re-renders.
  const isHighlighted = useQuotePresenceStore(
    s => s.highlightedItemId === itemId
  );

  const rowProps = useMemo(
    () => ({
      onMouseOver: (e: MouseEvent) => {
        e.stopPropagation();
        setHoveredItemId(itemId);
      },
      onMouseLeave: () => {
        if (useQuotePresenceStore.getState().hoveredItemId === itemId) {
          setHoveredItemId(null);
        }
      }
    }),
    [itemId, setHoveredItemId]
  );

  return { members, rowProps, isHighlighted };
};

/**
 * Avatar cluster pinned to the top-right of a row, shown only when other members
 * are focused on it. Render as the first child of a `position: relative` row.
 */
export const QuoteRowPresenceBadge = ({
  members
}: {
  members?: SpaceMember[];
}) => {
  if (!members?.length) return null;
  return (
    <Box style={{ position: 'absolute', top: 4, right: 4, zIndex: 10 }}>
      <RowPresence members={members} />
    </Box>
  );
};
